import { authOperations, authSelectors, hooks, mainSelectors } from 'state';
import { Outlet, useNavigate } from 'react-router-dom';
import { Container, Text } from 'components';
import { Header, Footer } from 'components/layout';
import { useEffect, useRef, useState } from 'react';
import { getUserFromSubcookies } from 'utils';
import StyledPrivateRoute from './private-route-styled';
import BottomNavigationBar from '../../layout/BottomNavigationBar';
import { allowedRoles } from 'utils/constants';
import { IAllowedRoles } from '../../../utils/constants/interfaces';
import { WelcomePopup } from '../../shared/WelcomePopup';

const WELCOME_POPUP_STORAGE_KEY = 'welcomePopupDismissCount'
const WELCOME_POPUP_MAX_SHOWS = 3

function getWelcomePopupDismissCount (user: { emailAddress?: string }): number {
  if (!user?.emailAddress) return 0
  try {
    const raw = localStorage.getItem(`${WELCOME_POPUP_STORAGE_KEY}_${user.emailAddress}`)
    const n = raw ? parseInt(raw, 10) : 0
    return Number.isNaN(n) ? 0 : Math.max(0, n)
  } catch {
    return 0
  }
}

function incrementWelcomePopupDismissCount (user: { emailAddress?: string }): number {
  if (!user?.emailAddress) return 0
  const next = getWelcomePopupDismissCount(user) + 1
  try {
    localStorage.setItem(`${WELCOME_POPUP_STORAGE_KEY}_${user.emailAddress}`, String(next))
  } catch {
    // ignore
  }
  return next
}

export const PrivateRoute = () => {
  const { useAppSelector, useAppDispatch } = hooks
  const { isAuthenticatedSelector, selectAuth } = authSelectors
  const { getUser } = authOperations
  const isAuthenticated = useAppSelector(isAuthenticatedSelector)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const userCookie = getUserFromSubcookies()
  const getValidTodken = () => {
    const {
      access_token,
      expires_at,
      scope,
      token_type,
    } = userCookie
    if (
      access_token
      && scope
      && token_type
    ) {
      const token = {
        access_token,
        expires_at,
        scope,
        token_type
      }
      return token
    }
    return false
  }

  const { selectmain } = mainSelectors
  const { isFetching, user } = useAppSelector(selectAuth)
  const { appType, healthStatus } = useAppSelector(selectmain)
  const roles = userCookie?.profile.role || []
  const hasActiveSubscription = roles.includes(allowedRoles[appType as keyof IAllowedRoles])
  const [showWelcomePopup, setShowWelcomePopup] = useState(false)
  const hasShownWelcomeRef = useRef(false)

  useEffect(() => {
    if (user && !hasShownWelcomeRef.current && getWelcomePopupDismissCount(user) < WELCOME_POPUP_MAX_SHOWS) {
      setShowWelcomePopup(true)
      hasShownWelcomeRef.current = true
    }
  }, [user])

  const handleCloseWelcomePopup = () => {
    if (user) incrementWelcomePopupDismissCount(user)
    setShowWelcomePopup(false)
  }

  useEffect(()=>{
    if(healthStatus && healthStatus.issues?.length > 0){
     navigate('/error')
    }else if(appType && hasActiveSubscription){
      const token = userCookie && getValidTodken()
      const dataTrackingSessionId = userCookie && userCookie.session_id
      dispatch(getUser({ token, dataTrackingSessionId }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when healthStatus changes to avoid redundant getUser
  },[healthStatus])




  const displayAppType = appType === 'insights' ? 'Insights' : appType === 'legacy_insights' ? 'Insights' : appType || 'Insights'

  return (isAuthenticated && !isFetching)
    ? (
      <StyledPrivateRoute expand className={"route"}>
        <Header />
        <Container className="body">
          <Outlet />
        </Container>
        <BottomNavigationBar />
        <Footer />
        <WelcomePopup
          isOpen={showWelcomePopup}
          onClose={handleCloseWelcomePopup}
          user={user || {}}
          appType={displayAppType}
        />
      </StyledPrivateRoute>
    )
    : <Text textStyle='h1'>Loading</Text>
}

export default PrivateRoute

